import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { toast } from 'sonner';
import { Maximize2 } from 'lucide-react';
import { Button } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './dialog';

/**
 * SignaturePad — reusable handwritten signature capture.
 *
 * Two modes are exposed via the tab strip at the top: **Draw** uses a
 * touch- and mouse-friendly canvas (same drawing logic as the survey
 * form we shipped earlier), **Upload** accepts a PNG/JPEG file (≤ 5 MB)
 * and previews it inline. Both routes produce the same shape — a
 * base-64 ``data:image/*`` URL — passed to :code:`onChange` so callers
 * don't need to care which method the operator picked.
 *
 * Props
 * ─────
 * • ``value``    — controlled data-URL (or null / empty string).
 * • ``onChange`` — called with a new data-URL, or ``null`` when cleared.
 * • ``height``   — canvas height in CSS pixels. Defaults to 120.
 * • ``testIdPrefix`` — every interactive control carries a data-testid
 *                     derived from this prefix so callers can drive
 *                     the pad from tests without wiring one-off ids.
 *
 * Imperative API (via ref):
 * • ``clear()`` — wipe the canvas / uploaded image.
 * • ``isEmpty()`` — true when nothing has been captured yet.
 */
const SignaturePad = forwardRef(({ value, onChange, height = 120, width = 520, testIdPrefix = 'sig', savedSignature = null, onSaveDefault = null, allowExpand = true }, ref) => {
  const [tab, setTab] = useState('draw');
  const [expanded, setExpanded] = useState(false);
  // Phones get a much taller, near-square pad — a 520×120 strip is
  // impossible to sign with a finger.
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 639px)').matches
      : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 639px)');
    const onChangeMq = (e) => setNarrow(e.matches);
    mq.addEventListener('change', onChangeMq);
    return () => mq.removeEventListener('change', onChangeMq);
  }, []);
  const padW = narrow ? 380 : width;
  const padH = narrow ? 220 : height;
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  // The data-URL currently painted on this canvas. Without it we cannot
  // tell our own ink apart from an update that arrived from elsewhere
  // (the "Sign large" window is a second pad bound to the same value).
  const paintedRef = useRef(null);
  const fileInputRef = useRef(null);

  const getXY = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    // The canvas bitmap (width/height attributes) is usually smaller
    // than its CSS box, so pointer coordinates must be scaled into
    // bitmap space — otherwise the ink lands left of the cursor.
    const sx = rect.width ? canvas.width / rect.width : 1;
    const sy = rect.height ? canvas.height / rect.height : 1;
    return { x: (t.clientX - rect.left) * sx, y: (t.clientY - rect.top) * sy };
  };
  const startDraw = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    const { x, y } = getXY(e);
    ctx.moveTo(x, y);
  };
  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getXY(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };
  const stopDraw = () => {
    if (!drawing.current) return;
    drawing.current = false;
    // Commit whatever's on the canvas to the parent so its "signed"
    // state flips the moment the pen lifts.
    try {
      const url = canvasRef.current.toDataURL('image/png');
      paintedRef.current = url;
      if (onChange) onChange(url);
    } catch (_) { /* ignore serialisation errors on stale canvases */ }
  };

  const isCanvasEmpty = () => {
    const c = canvasRef.current;
    if (!c) return true;
    const px = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
    for (let i = 3; i < px.length; i += 4) if (px[i] !== 0) return false;
    return true;
  };

  const clearAll = useCallback(() => {
    if (canvasRef.current) {
      const c = canvasRef.current;
      c.getContext('2d').clearRect(0, 0, c.width, c.height);
    }
    paintedRef.current = null;
    if (onChange) onChange(null);
  }, [onChange]);

  // Mirror the incoming ``value`` onto the canvas. This runs for every
  // change — including the second, third… stroke drawn in the big signing
  // window — but never while this pad's own pen is down, and never for
  // ink we painted ourselves. The old guard ("only paint an empty
  // canvas") meant the small pad froze on the very first stroke.
  useEffect(() => {
    if (tab !== 'draw' || !canvasRef.current) return;
    if (drawing.current) return;
    if (value === paintedRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    if (!value) {
      ctx.clearRect(0, 0, c.width, c.height);
      paintedRef.current = null;
      return;
    }
    const img = new window.Image();
    img.onload = () => {
      try {
        ctx.clearRect(0, 0, c.width, c.height);
        // Fit and centre — the pads come in different shapes, so a raw
        // paste would land the signature tiny in the corner.
        const scale = Math.min(c.width / img.width, c.height / img.height, 1);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (c.width - w) / 2, (c.height - h) / 2, w, h);
        paintedRef.current = value;
      } catch (_e) { /* ignore render race */ }
    };
    img.src = value;
  }, [value, tab, padW, padH]);

  useImperativeHandle(ref, () => ({
    clear: clearAll,
    isEmpty: () => (!value) && (tab !== 'draw' || isCanvasEmpty()),
  }));

  const handleFile = (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/.test(file.type)) {
      toast.error('Signature must be a PNG or JPEG image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Signature file exceeds 5 MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { if (onChange) onChange(reader.result); };
    reader.onerror = () => toast.error('Could not read the uploaded file');
    reader.readAsDataURL(file);
  };

  return (
    <div className="border rounded p-2 bg-white" data-testid={`${testIdPrefix}-container`}>
      {/* Wraps on purpose: "Save as default" only appears once something has
          been signed, and on a phone that extra button must push to a new
          line instead of crushing Draw / Upload / Clear. */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-y-1.5 gap-x-2">
        <div className="inline-flex rounded border overflow-hidden text-xs">
          {['draw', 'upload'].map(k => (
            <button
              type="button"
              key={k}
              onClick={() => setTab(k)}
              className={`px-3 py-1.5 sm:px-2 sm:py-0.5 ${tab === k ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
              data-testid={`${testIdPrefix}-tab-${k}`}
            >
              {k === 'draw' ? 'Draw' : 'Upload'}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {savedSignature && (
            <button
              type="button"
              onClick={() => { if (onChange) onChange(savedSignature); }}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1.5 sm:p-0"
              title="Load the signature saved in your profile"
              data-testid={`${testIdPrefix}-use-saved`}
            >Use saved</button>
          )}
          {onSaveDefault && value && (
            <button
              type="button"
              onClick={() => onSaveDefault(value)}
              className="text-xs text-slate-600 hover:text-slate-900 px-2 py-1.5 sm:p-0"
              title="Save this signature as your default"
              data-testid={`${testIdPrefix}-save-default`}
            >Save as default</button>
          )}
          {allowExpand && !narrow && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-xs font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
              title="Open a larger signing window"
              data-testid={`${testIdPrefix}-expand`}
            >
              <Maximize2 className="h-3 w-3" /> Sign large
            </button>
          )}
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-slate-500 hover:text-slate-800 px-2 py-1.5 sm:p-0"
            data-testid={`${testIdPrefix}-clear`}
          >Clear</button>
        </div>
      </div>

      {tab === 'draw' && (
        <canvas
          ref={canvasRef}
          height={padH}
          width={padW}
          onMouseDown={startDraw}
          onMouseMove={moveDraw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={moveDraw}
          onTouchEnd={stopDraw}
          className={`w-full border border-dashed border-slate-300 rounded bg-slate-50 touch-none ${narrow ? 'h-[220px]' : ''}`}
          data-testid={`${testIdPrefix}-canvas`}
        />
      )}

      {tab === 'upload' && (
        <div className="flex items-center gap-3 min-h-[120px]">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="text-xs"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`${testIdPrefix}-file`}
          />
          {value && (
            <div className="border rounded p-1 bg-slate-50">
              <img src={value} alt="signature preview" className="max-h-24 object-contain" />
            </div>
          )}
        </div>
      )}

      {tab === 'draw' && (
        <p className="text-[10px] text-slate-400 mt-1">
          Draw with the mouse or a finger — release to save.
          {allowExpand && !narrow && ' Need more room? Use “Sign large”.'}
        </p>
      )}

      {/* Large signing window — same pad, much bigger canvas. */}
      {allowExpand && (
        <Dialog open={expanded} onOpenChange={setExpanded}>
          <DialogContent className="max-w-4xl" data-testid={`${testIdPrefix}-expand-dialog`}>
            <DialogHeader>
              <DialogTitle>Sign here</DialogTitle>
            </DialogHeader>
            <SignaturePad
              value={value}
              onChange={onChange}
              height={340}
              width={1040}
              allowExpand={false}
              testIdPrefix={`${testIdPrefix}-big`}
              savedSignature={savedSignature}
              onSaveDefault={onSaveDefault}
            />
            <DialogFooter>
              <Button onClick={() => setExpanded(false)} data-testid={`${testIdPrefix}-expand-done`}>
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
